import {
  CompanyAffiliation,
  CompanyPermissions,
  TeamAndDetails,
  TeamRoleAndDetails,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/company';
import { logError } from './error';
import { ICompany, ICompanyAffiliation, IEmployeeContracts, IEmployeeWithContractDetails, IUser } from 'types';
import { DropdownValue } from 'components/generic-components/dropdown/type';
import { isDateInPast } from './dateTime';
import { IRoleType, ISubRoleType } from 'context/user/user.type';
import { ContractType } from 'lib/constants';
import { getTeamByContractId } from './rolesPermissions';

export const LS_key_Current_loggedIn_companyId = 'currentLoggedInAsCompanyId';
export const LS_key_Current_loggedIn_as_RoleType = 'currentLoggedInAsRoleType';
export const LS_key_Current_loggedIn_as_SubRoleType = 'currentLoggedInAsSubRoleType';
export const LS_key_Last_Used_Auth_Method = 'lastUsedAuthMethod';

type TeamId = string;

interface ChangeResult {
  rolesToAdd?: Record<TeamId, TeamRoleAndDetails>;
  teamsToRemove?: TeamId[];
}

export function calculateTeamRoleChanges(
  currentAssignedTeams: Record<TeamId, TeamRoleAndDetails>,
  initialAssignedTeams: Record<TeamId, TeamRoleAndDetails>,
): ChangeResult | null {
  const result: ChangeResult = {};

  // Check for teams to remove
  result.teamsToRemove = Object.keys(initialAssignedTeams).filter((teamId) => !(teamId in currentAssignedTeams));

  // Check for roles to add
  result.rolesToAdd = {};
  for (const teamId in currentAssignedTeams) {
    const currentTeam = currentAssignedTeams[teamId];
    const initialTeam = initialAssignedTeams[teamId];

    if (!initialTeam) {
      // Team is not present in initialAssignedTeams, add it to rolesToAdd
      result.rolesToAdd[teamId] = currentTeam;
    } else if (
      currentTeam.role !== initialTeam.role ||
      JSON.stringify(currentTeam.permissions) !== JSON.stringify(initialTeam.permissions)
    ) {
      // Team role or permissions have changed, add it to rolesToAdd
      result.rolesToAdd[teamId] = currentTeam;
    }
  }

  // If no changes detected, return null
  if (Object.keys(result.rolesToAdd).length === 0 && result.teamsToRemove.length === 0) {
    return null;
  }

  return result;
}

export interface IOption {
  label: 'Admin' | 'Manager' | 'Contractor' | 'Employee' | 'Employee (EOR)';
  type: IRoleType;
  subRoleType?: ISubRoleType;
  companyId: string;
}

export const getAccountSwitchingOptions = (affiliation: ICompanyAffiliation) => {
  const hasContractorContract = affiliation?.contractorContracts?.length;
  const hasW2EmployeeContract = affiliation?.w2Contracts?.length;
  const hasEORContract = affiliation?.eorContracts?.length;
  const isOrgAdmin =
    affiliation?.orgRoleAndDetails?.length === 1 && affiliation.orgRoleAndDetails[0].role === 'role_admin'
      ? true
      : false;

  // @ts-ignore
  const companyID = affiliation?.company?.companyID;
  const canUseAsManager =
    (affiliation?.teams && Object.keys(affiliation?.teams).length > 0) ||
    affiliation?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_admin') ||
    affiliation?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_manager') ||
    affiliation?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_apar_manager') ||
    affiliation?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_org_manager');
  const options: IOption[] = [];
  if (canUseAsManager) {
    options.push({
      label: isOrgAdmin ? 'Admin' : 'Manager',
      type: 'manager',
      companyId: companyID,
    });
  }

  if (hasContractorContract) {
    options.push({
      label: 'Contractor',
      type: 'worker',
      subRoleType: 'contractor',
      companyId: companyID,
    });
  }
  if (hasEORContract) {
    options.push({
      label: 'Employee (EOR)',
      type: 'worker',
      subRoleType: 'eorEmployee',
      companyId: companyID,
    });
  }
  if (hasW2EmployeeContract) {
    options.push({
      label: 'Employee',
      type: 'worker',
      subRoleType: 'w2Employee',
      companyId: companyID,
    });
  }

  return options;
};

/**
 * check local storage for current roleType and companyId
 * */
export const get_ls_saved_values = () => {
  if (typeof window === 'undefined') return null;
  if (!window?.localStorage) return null;
  try {
    const saved_subRoleType_val = window.localStorage.getItem(LS_key_Current_loggedIn_as_SubRoleType);
    const saved_roleType_val = window.localStorage.getItem(LS_key_Current_loggedIn_as_RoleType);
    const saved_companyId_val = window.localStorage.getItem(LS_key_Current_loggedIn_companyId);

    const saved_subRoleType = saved_subRoleType_val === 'undefined' ? null : saved_subRoleType_val;
    const saved_roleType = saved_roleType_val === 'undefined' ? null : saved_roleType_val;
    const saved_companyId = saved_companyId_val === 'undefined' ? null : saved_companyId_val;

    return {
      subRoleType: saved_subRoleType as ISubRoleType,
      roleType: saved_roleType as IRoleType,
      companyId: saved_companyId as string,
    };
  } catch (error) {
    logError(error);
  }
};

export const getEmployeeWithContractAndDocument = (
  contractId: string,
  employeeContracts: IEmployeeWithContractDetails,
) => {
  const contractAndContractDocument = employeeContracts?.contractsAndContractDocuments?.find(
    (item) => item.contract.id === contractId,
  );
  const employeeWithContract = {
    employee: employeeContracts?.employee,
    contract: contractAndContractDocument?.contract,
    document: contractAndContractDocument?.document,
    company: employeeContracts?.company,
  };
  return employeeWithContract;
};

export const checkIfManagerWithoutTeams = (
  roleType: 'manager' | 'worker',
  affiliationsByCompany: Record<string, CompanyAffiliation>,
  currentCompany: ICompany,
  isOrgAdminOrHRSpecialist?: boolean,
) => {
  if (isOrgAdminOrHRSpecialist) return false;
  const currentCompanyTeams = affiliationsByCompany?.[currentCompany?.companyID]?.teams;
  return roleType === 'manager' && (!currentCompanyTeams || Object.keys(currentCompanyTeams)?.length === 0);
};

export const getTeamMemberOptions = (
  managers: {
    user: IUser;
    companyPermissions: CompanyPermissions;
  }[],
  selectedTeamId: string,
): DropdownValue[] => {
  const options = [];
  managers?.forEach((manager) => {
    const isTeamMember = manager.companyPermissions.teams[selectedTeamId]?.role;
    // if not team member, don't show
    if (isTeamMember)
      options.push({
        label: `${manager.user.firstName} ${manager.user.lastName}`,
        value: manager.user.userID,
        id: manager.user.userID,
        additionalValue: manager.user.email,
      });
  });
  return options;
};

export const getOrgAdminsOptions = (
  managers: {
    user: IUser;
    companyPermissions: CompanyPermissions;
  }[],
): DropdownValue[] => {
  const options = [];
  managers?.forEach((manager) => {
    const isOrgAdmin = manager.companyPermissions.org[0].role === 'role_admin';
    // if not org admin, don't show
    if (isOrgAdmin)
      options.push({
        label: `${manager.user.firstName} ${manager.user.lastName}`,
        value: manager.user.userID,
        id: manager.user.userID,
        additionalValue: manager.user.email,
      });
  });
  return options;
};

export const getAvailableTeamsToSwitch = (
  allTeams: Record<string, TeamAndDetails>,
  companyTeams: Record<string, TeamRoleAndDetails>,
) => {
  if (!allTeams) return {};
  // collect only teams that are available in the company
  const availableTeams: Record<string, TeamAndDetails> = Object.keys(allTeams).reduce((acc, teamId) => {
    if (companyTeams?.[teamId]) {
      acc[teamId] = allTeams[teamId];
    }
    return acc;
  }, {});
  return availableTeams;
};

/**
 * Get the number of active contracts for a team
 * @param teamContractIds - array of contract ids string[] (both active and inactive)
 * @param companyContracts - array of company contracts IEmployeeContract[]
 * @returns number
 * */
export const getActiveContractsCount = (teamContractIds: string[], companyContracts: IEmployeeContracts[]) => {
  if (!teamContractIds?.length) return 0;
  if (!companyContracts?.length) return 0;
  const activeCompanyContracts = companyContracts
    ?.flatMap((item) => item.employments.map((employment) => employment))
    ?.filter((contract) => !isDateInPast(contract?.terminationDetails?.endDate));
  const activeContracts = activeCompanyContracts?.filter((contract) => teamContractIds?.includes(contract.id));
  return activeContracts?.length;
};

export const getW2Contract = (contract: IEmployeeContracts) => {
  const w2Contract = contract?.employments?.find((employment) => Boolean(employment.w2Payroll?.checkCompanyId));
  return w2Contract;
};

export const getAllContractorContracts = (contract: IEmployeeContracts | IEmployeeWithContractDetails) => {
  if ((contract as IEmployeeWithContractDetails)?.contractsAndContractDocuments) {
    const contractorContracts = (contract as IEmployeeWithContractDetails).contractsAndContractDocuments?.filter(
      (contractAndContractDocument) => contractAndContractDocument?.contract?.type === 'CONTRACTOR',
    );
    return contractorContracts;
  }

  const contractorContracts = (contract as IEmployeeContracts)?.employments.filter(
    (employment) => employment?.type === 'CONTRACTOR',
  );
  return contractorContracts;
};

export const getAllEORContracts = (contract: IEmployeeContracts | IEmployeeWithContractDetails) => {
  if ((contract as IEmployeeWithContractDetails)?.contractsAndContractDocuments) {
    const eorContracts = (contract as IEmployeeWithContractDetails).contractsAndContractDocuments?.filter(
      (contractAndContractDocument) => contractAndContractDocument?.contract?.type === 'EMPLOYEE',
    );
    return eorContracts;
  }

  const eorContracts = (contract as IEmployeeContracts)?.employments.filter(
    (employment) => employment?.type === 'EMPLOYEE',
  );
  return eorContracts;
};

/**
 * True if any employment across `employeeWithContracts` matches `contractType`. When
 * `teamIdsToFilter` is passed, only counts contracts belonging to one of those teams.
 */
export const hasContractType = (
  employeeWithContracts: IEmployeeContracts[],
  contractType: ContractType,
  teams: Record<string, TeamAndDetails>,
  teamIdsToFilter?: string[],
): boolean =>
  employeeWithContracts?.some((employeeAndEmployment) =>
    employeeAndEmployment.employments?.some((employment) => {
      if (employment?.contractType !== contractType) return false;
      if (!teamIdsToFilter?.length) return true;
      const team = getTeamByContractId(employment.id, teams);
      return teamIdsToFilter.includes(team.id);
    }),
  ) ?? false;
